import { useEffect, useMemo, useState } from 'react';
import type { AracDetay, IsEmriDetay } from '@nero/shared';
import { plakaGoster, plakaNormalle } from '@nero/shared';
import { api, useEylem } from '../../lib/veri.js';
import { sorgu } from '../../lib/api.js';
import { Pencere } from '../../bilesenler/Pencere.js';
import { Girdi, MetinAlani, ParaGirdi } from '../../bilesenler/form.js';
import { MusteriSecici } from '../../bilesenler/MusteriSecici.js';
import { UyariSerit } from '../../bilesenler/temel.js';

/**
 * HIZLI İŞ EMRİ
 *
 * Araç geldiğinde tek ekranda iş emri açar. Plaka yazılır: araç sistemde
 * kayıtlıysa müşteri ve araç bilgileri kendiliğinden gelir, personel aynı
 * bilgiyi ikinci kez girmez. Kayıtlı değilse yalnızca eksik alanlar sorulur.
 */
export function HizliIsEmri({
  kapat, olusturuldu,
}: { kapat: () => void; olusturuldu: (id: number) => void }) {
  const [plaka, setPlaka] = useState('');
  const [bulunan, setBulunan] = useState<AracDetay | null>(null);
  const [aranıyor, setAraniyor] = useState(false);
  const [arandi, setArandi] = useState(false);

  /* PLAKASIZ AKIŞTA KAYITLI MÜŞTERİ SEÇİMİ.
     Olmasaydı plakasız açılan her iş emri YENİ müşteri yaratırdı ve
     aynı kişi defalarca kaydedilirdi — depo zaten mükerrer müşteriden
     şikâyetçi (bkz. musteriPlakaKayit.test.js). */
  const [musteriId, setMusteriId] = useState('');
  const [musteriAd, setMusteriAd] = useState('');
  const [telefon, setTelefon] = useState('');
  const [marka, setMarka] = useState('');
  const [model, setModel] = useState('');
  const [motor, setMotor] = useState('');
  const [motorKodu, setMotorKodu] = useState('');
  const [kilometre, setKilometre] = useState('');
  const [islem, setIslem] = useState('');
  const [not, setNot] = useState('');
  const [tutar, setTutar] = useState('');
  const [aramaHatasi, setAramaHatasi] = useState<string | null>(null);

  const temizPlaka = plakaNormalle(plaka);

  /* Plaka yazıldıkça kayıtlı araç aranır — kısa bir bekleme ile. */
  /* Plaka yazıldıkça kayıtlı araç aranır — kısa bir bekleme ile.
     Plaka her değiştiğinde önceki sonuç HEMEN unutulur; yoksa kullanıcı
     plakayı düzeltip hızlıca kaydederse eski araca iş emri açılırdı. */
  useEffect(() => {
    setBulunan(null);
    setArandi(false);
    setAramaHatasi(null);
    if (temizPlaka.length < 4) { setAraniyor(false); return; }
    let iptal = false;
    setAraniyor(true);
    const zaman = window.setTimeout(async () => {
      try {
        const sonuc = await api.al<AracDetay[]>(`/araclar${sorgu({ plaka: temizPlaka })}`);
        if (iptal) return;
        setBulunan(sonuc[0] ?? null);
        setArandi(true);
      } catch (e) {
        /* Sorgu HATASI ile "araç kayıtlı değil" aynı şey DEĞİLDİR.
           Karıştırılırsa kullanıcı kayıtlı müşteriyi yeniden girer ve
           mükerrer müşteri/araç oluşur. */
        if (!iptal) {
          setBulunan(null);
          setArandi(false);
          setAramaHatasi(e instanceof Error ? e.message : 'Plaka sorgulanamadı');
        }
      } finally {
        if (!iptal) setAraniyor(false);
      }
    }, 350);
    return () => { iptal = true; window.clearTimeout(zaman); };
  }, [temizPlaka]);

  const yeniKayit = arandi && !bulunan && temizPlaka.length >= 4;
  /**
   * PLAKA GİRİLMEDİ — iş emri ARAÇSIZ açılacak.
   *
   * İSTEK: "Plaka girmeden … iş emri açma özelliği."
   * Eskiden plaka 4 karakterden kısaysa `gecerli` false kalıyor ve
   * "İş emrini aç" düğmesi HİÇ açılmıyordu (ölçüldü: düğme kapalı).
   * Artık plaka boşken müşteri yeter; araç sonradan eklenebilir.
   */
  const aracsiz = temizPlaka.length === 0;

  const gecerli = useMemo(() => {
    /* Arama sürerken ya da başarısızken kaydetmeye izin verme. */
    if (aranıyor || aramaHatasi) return false;
    const musteriTamam = !!musteriId || musteriAd.trim().length > 0;
    /* Plakasız: yalnız müşteri gerekir. */
    if (aracsiz) return musteriTamam;
    /* Yarım plaka (1-3 karakter) kabul edilmez: kullanıcı yazmaya
       başlamış ama bitirmemiştir; sessizce araçsız açmak yanlış olurdu. */
    if (temizPlaka.length < 4) return false;
    if (bulunan) return true;
    if (!arandi) return false;
    return musteriTamam;
  }, [aracsiz, temizPlaka, bulunan, musteriId, musteriAd, aranıyor, aramaHatasi, arandi]);

  const { yurut, calisiyor, hata } = useEylem(async () => {
    const ortak = {
      islem, not, tutar: Number(tutar) || 0,
      kilometre: kilometre ? Number(kilometre) : null,
    };
    /* MÜŞTERİ: kayıtlıysa kimlik, değilse yeni kayıt. */
    const musteriKismi = musteriId
      ? { musteri_id: Number(musteriId) }
      : { musteri: { ad: musteriAd.trim(), telefon, eposta: '', not: '' } };

    const govde = bulunan
      ? { musteri_id: bulunan.musteri_id, arac_id: bulunan.id, ...ortak }
      : aracsiz
        /* ARAÇ ALANI HİÇ GÖNDERİLMEZ. Boş bir `arac` nesnesi göndermek
           plakasız bir araç kaydı yaratırdı; Araçlar listesi boş
           satırlarla dolardı. Sunucu `arac` yoksa iş emrini araçsız
           açar (bkz. routes/isEmirleri.ts · /hizli). */
        ? { ...musteriKismi, ...ortak }
        : {
            ...musteriKismi,
            arac: {
              plaka: temizPlaka, marka, model, motor, motor_kodu: motorKodu,
              sanziman: '', vin: '', not: '', yil: null,
            },
            ...ortak,
          };
    return api.gonder<IsEmriDetay>('/is-emirleri/hizli', govde);
  });

  async function kaydet() {
    const sonuc = await yurut(undefined as never);
    if (sonuc) olusturuldu(sonuc.id);
  }

  return (
    <Pencere
      baslik="Yeni iş emri"
      alt="Plakayı yazın — araç kayıtlıysa bilgiler kendiliğinden gelir. Plaka isteğe bağlıdır."
      boyut="genis"
      kapat={kapat}
      altBolum={
        <>
          <button className="btn btn-sade" onClick={kapat} disabled={calisiyor}>Vazgeç</button>
          <button className="btn btn-birincil" onClick={kaydet} disabled={!gecerli || calisiyor}>
            {calisiyor ? 'Açılıyor…' : 'İş emrini aç'}
          </button>
        </>
      }
    >
      <div className="sutun">
        <Girdi
          etiket="Plaka"
          value={plaka}
          onChange={setPlaka}
          placeholder="27 ABC 123"
          autoFocus
          autoCapitalize="characters"
          duzen="plaka"
          /* `required` KALDIRILDI: plaka artık zorunlu değil. Araçsız iş
             emri açılabiliyor (bkz. aracsiz). */
          ipucu={aranıyor ? 'Kayıt aranıyor…'
            : aracsiz ? 'Boş bırakırsanız iş emri ARAÇSIZ açılır — aracı sonra ekleyebilirsiniz'
              : undefined}
        />

        {bulunan && (
          <UyariSerit ton="olumlu" baslik={`${bulunan.musteri_ad} · ${plakaGoster(bulunan.plaka)}`}>
            {[bulunan.marka, bulunan.model, bulunan.motor_kodu].filter(Boolean).join(' · ') || 'Kayıtlı araç'}
            {bulunan.musteri_telefon ? ` · ${bulunan.musteri_telefon}` : ''}
          </UyariSerit>
        )}

        {aramaHatasi && (
          <UyariSerit ton="kritik" baslik="Plaka sorgulanamadı">
            {aramaHatasi} — bağlantıyı kontrol edip plakayı tekrar yazın. Sorgu başarısızken
            yeni kayıt açılmaz; aksi hâlde kayıtlı müşteri ikinci kez oluşturulabilir.
          </UyariSerit>
        )}
        {/* ───── PLAKASIZ AKIŞ ─────
            Önce KAYITLI müşteri aranır; bulunmazsa yeni müşteri alanları
            açılır. Bu sıra bilinçlidir: doğrudan "yeni müşteri" sorsaydık
            her plakasız iş emri aynı kişiyi bir kez daha kaydederdi. */}
        {aracsiz && (
          <>
            <MusteriSecici
              deger={musteriId}
              onChange={(id, m) => {
                setMusteriId(id);
                /* Seçim kalkarsa elle yazılan ad silinmez; seçim
                   yapılırsa ad alanı zaten gizlenir. */
                if (m) { setMusteriAd(''); setTelefon(''); }
              }}
              etiket="Müşteri"
              yerTutucu="Kayıtlı müşteri ara — isim, telefon veya plaka…"
              bosMetin="Kayıtlı değilse aşağıya adını yazın"
            />
            {!musteriId && (
              <div className="izgara izgara-2">
                <Girdi etiket="Müşteri adı" value={musteriAd} onChange={setMusteriAd} required duzen="ozelAd" />
                <Girdi etiket="Telefon" value={telefon} onChange={setTelefon}
                  inputMode="tel" placeholder="05xx xxx xx xx" duzen="telefon" />
              </div>
            )}
          </>
        )}

        {yeniKayit && (
          <>
            <UyariSerit ton="notr" baslik="Bu plaka sistemde yok">
              Müşteri ve araç bu iş emriyle birlikte kaydedilecek.
            </UyariSerit>
            <div className="izgara izgara-2">
              <Girdi etiket="Müşteri adı" value={musteriAd} onChange={setMusteriAd} required duzen="ozelAd" />
              <Girdi etiket="Telefon" value={telefon} onChange={setTelefon} inputMode="tel" placeholder="05xx xxx xx xx" duzen="telefon" />
              <Girdi etiket="Marka" value={marka} onChange={setMarka} placeholder="BMW" duzen="markaModel" />
              <Girdi etiket="Model" value={model} onChange={setModel} placeholder="320i LCI" duzen="markaModel" />
              <Girdi etiket="Motor" value={motor} onChange={setMotor} placeholder="2.0 Turbo" duzen="markaModel" />
              <Girdi etiket="Motor kodu" value={motorKodu} onChange={setMotorKodu} placeholder="B48B16" duzen="bosluk" />
            </div>
          </>
        )}

        <div className="izgara izgara-2">
          <Girdi etiket="Kilometre" value={kilometre} onChange={setKilometre} type="number" inputMode="numeric" min={0} />
          <ParaGirdi etiket="Tutar" deger={tutar} onChange={setTutar} ipucu="Sonradan kalem ekleyerek de belirlenebilir" />
        </div>

        <Girdi etiket="Yapılacak işlem" value={islem} onChange={setIslem} placeholder="Stage 1 yazılım" duzen="cumle" />
        <MetinAlani etiket="Not" value={not} onChange={setNot} rows={3} placeholder="Müşterinin belirttiği şikâyet, özel istek…" duzen="cumle" />

        {hata && <UyariSerit ton="kritik">{hata}</UyariSerit>}
      </div>
    </Pencere>
  );
}
